import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScheduleEvent, ScheduleInputs } from '../../containers/Schedule/Schedule';
import { useForm } from 'react-hook-form';
import CactusLabelInput from '../Cactus/CactusLabelInput';
import useFormValidatorText from '../../hooks/validators/use-form-validator-text';
import moment from 'moment';
import 'moment/locale/es';
import Option from "../../models/Option";
import SelectAsyncPaginate from '../SelectAsyncPaginate';
import PlaylistService from '../../services/use_case/playlist/PlaylistService';
import Playlist, { getOption } from "../../models/Playlist";
import { CustomSelect } from "../../components";

interface CreateEditScheduleProps {
    disabled: boolean;
    selectedScheduleEvent: ScheduleEvent | null;
    playlistValue: Option | null;
    showDays?: Option[] | null;
    setUpdatingSchedule: (value: boolean) => void;
    setDeletingScheduleForm: (value: boolean) => void;
    onPlaylistChange: (playlistValue: Option | null) => void;
    onSubmit: (formData: any) => void;
};

const CreateEditSchedule: React.FC<CreateEditScheduleProps> = (props: CreateEditScheduleProps) => {

    const [t] = useTranslation();

    let playlistService: PlaylistService | null = null;

    const { handleSubmit, register, trigger, errors, setValue } = useForm<ScheduleInputs>({
        shouldUnregister: false,
        defaultValues: {
            start_date: props.selectedScheduleEvent?.start ? moment(props.selectedScheduleEvent?.start).format("YYYY-MM-DDTHH:mm") : undefined,
            end_date: props.selectedScheduleEvent?.end && moment(props.selectedScheduleEvent.end).format("YYYY-MM-DD") !== '9999-12-31'
            ? moment(props.selectedScheduleEvent?.end).format("YYYY-MM-DDTHH:mm")
            : undefined,
            start_time: props.selectedScheduleEvent?.start_time ?? '',
            end_time: props.selectedScheduleEvent?.end_time ?? '',
            priority: props.selectedScheduleEvent?.priority ?? 0,
        }
    });
    const textValidator = register(useFormValidatorText());
    
    const [playlistValue, setPlaylistValue] = useState(props.playlistValue);
    const [selectedDays, setSelectedDays] = useState<Option[]>(props.showDays ?? []);
    const [priorityValue, setPriorityValue] = useState<number>(Number(props.selectedScheduleEvent?.priority ?? 0));

    const handlePriorityChange = (val: number) => {
        setPriorityValue(val);
        setValue('priority', val);
    };

    const isEditingSchedule = props.selectedScheduleEvent !== null;

    const [useCustomTimeSlots, setUseCustomTimeSlots] = useState<boolean>(false);
    const [useEndDate, setUseEndDate] = useState<boolean>(false);

    const [stepIndex, setStepIndex] = useState(0);

    useEffect(() => {
        props.onPlaylistChange(playlistValue);
    }, [playlistValue]);

    useEffect(() => {
        if (isEditingSchedule && props.selectedScheduleEvent) {
            const isAlways = props.selectedScheduleEvent.is_always_schedule;
            setUseEndDate(!isAlways);
        }
    }, [isEditingSchedule, props.selectedScheduleEvent]);

    useEffect(() => {
        if (isEditingSchedule && props.selectedScheduleEvent) {
            setUseCustomTimeSlots(props.selectedScheduleEvent.is_time_slot);
        }
    }, [isEditingSchedule, props.selectedScheduleEvent]);

    useEffect(() => {
        if (!useEndDate) {
            const input = document.querySelector<HTMLInputElement>('input[name="end_date"]');
            if (input) input.value = "";
        }
    }, [useEndDate]);

    useEffect(() => {
        if (!useCustomTimeSlots) {
            const startTimeInput = document.querySelector<HTMLInputElement>('input[name="start_time"]');
            const endTimeInput = document.querySelector<HTMLInputElement>('input[name="end_time"]');
            if (startTimeInput) startTimeInput.value = "";
            if (endTimeInput) endTimeInput.value = "";
        }
    }, [useCustomTimeSlots]);


    useEffect(() => {
        if (props.showDays && props.selectedScheduleEvent?.showDays) {
            const filteredDays = props.showDays.filter((dayOption) =>
                dayOption.value !== null && props.selectedScheduleEvent?.showDays.includes(dayOption.value)
            );
            setSelectedDays(filteredDays);
        }
    }, [props.showDays, props.selectedScheduleEvent]);

    const _fetchPlaylists = async (search: string, prevOptions: any = null, { page }: { page: number }) => {
        try {
            playlistService = new PlaylistService();
            let response: any = await playlistService.fetch(page, search);
            const responseData = response.data?.data;
            let playlists: Playlist[] = responseData?.data ?? [];

            return {
                options: [{ label: '-', value: null }, ...playlists.map((playlist) => getOption(playlist))],
                hasMore: responseData?.last_page > page,
                additional: { page: responseData.current_page + 1 },
            };
        } catch {
            return {
                options: [],
                hasMore: false,
                additional: { page: 1 },
            };
        }
    };

    const _returnStartDate = (): string => {
        if (!props.selectedScheduleEvent?.start) return "";
        return moment(props.selectedScheduleEvent?.start).format("YYYY-MM-DDTHH:mm");
    }

    const _returnEndDate = (): string => {
        if (!props.selectedScheduleEvent?.end || moment(props.selectedScheduleEvent?.end).format("YYYY-MM-DD") === '9999-12-31') return "";
        return moment(props.selectedScheduleEvent?.end).format("YYYY-MM-DDTHH:mm");
    }

    const _renderBtnSubmit = () => (
        <button type="submit" className="btn btn-cactus mt-2 ml-auto">
            {t('Create_schedule')}
        </button>
    );

    const _renderBtnDelete = () => (
        <button type="button" onClick={() => props.setDeletingScheduleForm(true)} className="btn btn-danger mt-2">
            {t('Delete_schedule')}
        </button>
    );

    const _renderBtnUpdate = () => (
        <button type="submit" onClick={() => props.setUpdatingSchedule(true)} className="btn btn-cactus mt-2 ml-auto">
            {t('Update_schedule')}
        </button>
    );

    const _renderBtnNext = () => (
        <button
            type="button"
            className="btn btn-cactus ml-auto"
            onClick={async () => {
                const fieldsPerStep: Record<number, string[]> = {
                0: ['name', 'priority'],
                1: ['start_date', ...(useEndDate ? ['end_date'] : []), ...(useCustomTimeSlots ? ['start_time', 'end_time'] : [])],
                2: [],
                };

                const currentFields = fieldsPerStep[stepIndex];
                const isValid = await trigger(currentFields);

                if (isValid) {
                setStepIndex(stepIndex + 1);
                }
            }}
            >
            {t('Next')}
        </button>
    )

    const _renderBtnBack = () => (
        <button type="button" className="btn btn-cactus--red" onClick={() => setStepIndex(stepIndex - 1)}>
            {t('Back')}
        </button>
    )
    const _renderScheduleStep1 = () => (
        <>
            <div className='mt-2'>
                <CactusLabelInput
                    labelTitle={t('Schedule_name')}
                    inputAttributes={{
                        id: "name",
                        name: "name",
                        type: "text",
                        defaultValue: props.selectedScheduleEvent?.title,
                        placeholder: t('Name'),
                        autoComplete: "name",
                        ref: textValidator,
                    }}
                    errorMessage={errors.name ? errors.name.message : null}
                />
            </div>

            <div className='mt-2 mb-2'>
                <label>
                    <span className="form__label">
                        {t('Playlist')}
                    </span>
                </label>
                <SelectAsyncPaginate
                    fetchOptions={_fetchPlaylists}
                    value={playlistValue}
                    onChange={(selectedOption: Option) => setPlaylistValue(selectedOption)}
                />
            </div>

            {props.selectedScheduleEvent?.id && (
                <div className='mt-2'>
                    <CactusLabelInput
                        labelTitle={t('Screen/Screen Group')}
                        inputAttributes={{
                            id: "device_name",
                            name: "device_name",
                            type: "text",
                            defaultValue: props.selectedScheduleEvent?.device_name,
                            placeholder: t('Screen/Screen Group'),
                            autoComplete: "device_name",
                            disabled: true,
                            ref: textValidator,
                        }}
                        errorMessage={errors.name ? errors.name.message : null}
                    />
                </div>
            )}

            <div className='mt-3 mb-2'>
                <label>
                    <span className="form__label">
                        {t('Priority')}
                    </span>
                </label>
                <input type="hidden" name="priority" ref={register} />
                <div className="schedule-priority-selector">
                    {[0, 1, 2].map((val) => (
                        <span
                            key={val}
                            className={`event-priority-segment${val <= priorityValue ? ' event-priority-segment--filled' : ''}`}
                            onClick={() => handlePriorityChange(val)}
                        />
                    ))}
                </div>
            </div>
        </>
    );

    const _renderScheduleStep2 = () => (
        <>
            <div className="row">
                <div className='col-12'>
                    <label>
                        <span className="form__label">
                            {t('Schedule_with_end_date')}
                        </span>
                    </label>
                    <CustomSelect
                        options={[
                            { label: t('Yes'), value: 'yes' },
                            { label: t('No'), value: 'no' }
                        ]}
                        value={useEndDate ? { label: t('Yes'), value: 'yes' } : { label: t('No'), value: 'no' }}
                        widthDefaultOption={false}
                        onChange={(option: Option) => {
                            setUseEndDate(option.value === 'yes');
                        }}
                    />
                </div>
            </div>
            <div className='row mt-3'>
                <div className='col-6'>
                    <CactusLabelInput
                        labelTitle={t('Schedule_startDate')}
                        noW100={true}
                        inputAttributes={{
                            classprops: 'date-picker',
                            id: "start_date",
                            name: "start_date",
                            type: "datetime-local",
                            defaultValue: _returnStartDate(),
                            autoComplete: "start_date",
                            ref: textValidator,
                        }}
                        errorMessage={errors.name ? errors.name.message : null}
                    />
                </div>

                {useEndDate && (
                    <div className='col-6'>
                        <CactusLabelInput
                            labelTitle={t('Schedule_endDate')}
                            noW100={true}
                            inputAttributes={{
                                classprops: 'date-picker',
                                id: "end_date",
                                name: "end_date",
                                type: "datetime-local",
                                defaultValue: _returnEndDate(),
                                autoComplete: "end_date",
                                ref: textValidator,
                            }}
                            errorMessage={errors.name ? errors.name.message : null}
                        />
                    </div>
                )}
            </div>

            <div className='row mt-3'>
                <div className="col-12">
                    <label>
                        <span className="form__label">
                            {t('Do_you_want_custom_time_slots')}
                        </span>
                    </label>
                    <CustomSelect
                        options={[
                            { label: t('Yes'), value: 'yes' },
                            { label: t('No'), value: 'no' }
                        ]}
                        value={useCustomTimeSlots ? { label: t('Yes'), value: 'yes' } : { label: t('No'), value: 'no' }}
                        widthDefaultOption={false}
                        onChange={(option: Option) => {
                            setUseCustomTimeSlots(option.value === 'yes');
                        }}
                    />
                </div>
            </div>

            {useCustomTimeSlots && (
                <>
                    <div className="row">
                        <div className='col-6 mt-3'>
                            <CactusLabelInput
                                labelTitle={t('start_time_slot')}
                                noW100={true}
                                inputAttributes={{
                                    classprops: 'date-picker',
                                    id: "start_time",
                                    name: "start_time",
                                    type: "time",
                                    defaultValue: props.selectedScheduleEvent?.start_time,
                                    autoComplete: "start_time",
                                    ref: textValidator,
                                }}
                                errorMessage={errors.name ? errors.name.message : null}
                            />
                        </div>
                        <div className='col-6 mt-3'>
                            <CactusLabelInput
                                labelTitle={t('end_time_slot')}
                                noW100={true}
                                inputAttributes={{
                                    classprops: 'date-picker',
                                    id: "end_time",
                                    name: "end_time",
                                    type: "time",
                                    defaultValue: props.selectedScheduleEvent?.end_time,
                                    autoComplete: "end_time",
                                    ref: textValidator,
                                }}
                                errorMessage={errors.name ? errors.name.message : null}
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    );

    const _renderScheduleStep3 = () => (
        <>
            <div className='row mt-3'>
                 <div className="col-12">
                    <label>
                        <span className="form__label">
                            {t('Whats days do we want to show content')}
                        </span>
                    </label>
                    <CustomSelect
                        config={{ isMulti: true }}
                        options={props.showDays ?? []}
                        value={selectedDays}
                        widthDefaultOption={false}
                        onChange={(selectedOption: Option[]) => setSelectedDays(selectedOption)}
                    />
                 </div>
            </div>
        </>
    );

    return (
        <div>
            {isEditingSchedule && (
                <div className="tabs mb-3">
                    <nav className="nav nav-tabs">
                        <button type="button" className={`nav-link ${stepIndex === 0 ? 'active' : ''}`} onClick={() => setStepIndex(0)}>
                            {t('Basic info')}
                        </button>
                        <button type="button" className={`nav-link ${stepIndex === 1 ? 'active' : ''}`} onClick={() => setStepIndex(1)}>
                            {t('Schedule settings')}
                        </button>
                        <button type="button" className={`nav-link ${stepIndex === 2 ? 'active' : ''}`} onClick={() => setStepIndex(2)}>
                            {t('Days to show')}
                        </button>
                    </nav>
                </div>
            )}

            <form onSubmit={handleSubmit((formData) => {
                props.onSubmit({
                    ...formData,
                    show_days: selectedDays.map((day) => day.value),
                    useCustomTimeSlots,
                    useEndDate
                });
            })}>
                {!isEditingSchedule ? (
                    <>
                        {stepIndex === 0 && _renderScheduleStep1()}
                        {stepIndex === 1 && _renderScheduleStep2()}
                        {stepIndex === 2 && _renderScheduleStep3()}

                        <div className="d-flex justify-content-between mt-4">
                            {stepIndex > 0 && (
                                _renderBtnBack()
                            )}
                            {stepIndex < 2 && (
                                _renderBtnNext()
                            )}
                            {stepIndex === 2 && _renderBtnSubmit()}
                        </div>
                    </>
                ) : (
                    <>
                        {stepIndex === 0 && _renderScheduleStep1()}
                        {stepIndex === 1 && _renderScheduleStep2()}
                        {stepIndex === 2 && _renderScheduleStep3()}

                        <div className="row mt-4">
                            <div className="col-6 d-flex">
                                {_renderBtnDelete()}
                            </div>
                            <div className="col-6 d-flex">
                                {_renderBtnUpdate()}
                            </div>
                        </div>
                    </>
                )}
            </form>
        </div>
    );
};

export default React.memo(CreateEditSchedule);
